import SelectInput, {
    SelectOption,
} from '@components/common/SelectInput/SelectInput';
import DatePicker from '@components/common/DatePicker';
import TextInput from '@components/common/TextInput';
import { farmingHist, FarmlandAddReq } from '@/api/growth/farmland/type';
import { useState } from 'react';
import { formatDateString, parseDate } from '@utils/Utils';

interface PestMngProps {
    formData: FarmlandAddReq;
    onInputChange: (field: string, value: farmingHist[]) => void;
}

const PestMng = ({ formData, onInputChange }: PestMngProps) => {
    const [farmingHists, setFarmingHists] = useState<farmingHist[]>(
        formData.farmingHistList?.filter(h => h.category === 'C') || [
            {
                category: 'C',
                workDt: '',
                iamt: '',
                iame: '',
                chcod: '',
                chamt: '',
                chme: '',
                erad: '',
                emax: '',
                emin: '',
                ewind: '',
                ename: '',
                fmcd: '',
                facd: '',
                famn: '',
                famp: '',
                famk: '',
                rcod: '',
                ramt: '',
                rdep: '',
                timpl: '',
                tdep: '',
            },
        ],
    );

    const handleFarmingHistChange = (
        index: number,
        field: keyof farmingHist,
        value: string | number,
    ) => {
        const updated = [...farmingHists];
        updated[index] = { ...updated[index], [field]: value };
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const updateParentFarmingHistList = (pestData: farmingHist[]) => {
        const otherData =
            formData.farmingHistList?.filter(h => h.category !== 'C') || [];
        const allData = [...otherData, ...pestData];
        onInputChange('farmingHistList', allData);
    };

    const addFarmingHist = () => {
        const newRecord: farmingHist = {
            category: 'C',
            workDt: '',
            iamt: '',
            iame: '',
            chcod: '',
            chamt: '',
            chme: '',
            erad: '',
            emax: '',
            emin: '',
            ewind: '',
            ename: '',
            fmcd: '',
            facd: '',
            famn: '',
            famp: '',
            famk: '',
            rcod: '',
            ramt: '',
            rdep: '',
            timpl: '',
            tdep: '',
        };
        const updated = [...farmingHists, newRecord];
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const removeFarmingHist = (index: number) => {
        const updated = farmingHists.filter((_, i) => i !== index);
        setFarmingHists(updated);
        updateParentFarmingHistList(updated);
    };

    const pesticideOptions: SelectOption[] = [
        {
            value: 'CH001',
            label: '[제초제] 알라클로르, 메톨라클로르 - 토양처리 제초제',
        },
        { value: 'CH002', label: '[제초제] 프로파닐 - 경엽처리 제초제' },
        { value: 'CH003', label: '[제초제] 트리플루랄린 - 토양처리 제초제' },
        { value: 'CH004', label: '[제초제] 달라폰 - 화본과 잡초 방제용' },
        { value: 'CH005', label: '[제초제] MCPA - 선택성 제초제' },
        { value: 'CH006', label: '[제초제] 2,4-D - 광엽잡초 제거제' },
        { value: 'CH007', label: '[제초제] 2,4,5-T - 광엽잡초 및 관목 제거제' },
        { value: 'CH008', label: '[제초제] 펜디메탈린 - 토양처리 제초제' },
        { value: 'CH009', label: '[제초제] 아트라진 - 광엽잡초 방제용' },
        { value: 'CH010', label: '[제초제] 디쿼트 - 비선택성 접촉형 제초제' },
        { value: 'CH011', label: '[제초제] 파라쿼트 - 비선택성 접촉형 제초제' },
        { value: 'CH021', label: '[살충제] 카바릴 - 광범위 살충제' },
        { value: 'CH022', label: '[살충제] 말라티온 - 유기인계 살충제' },
        { value: 'CH023', label: '[살충제] 네일드 - 유기인계 살충제' },
        { value: 'CH024', label: '[살충제] 디메토에이트 - 침투이행성 살충제' },
        { value: 'CH025', label: '[살충제] 펜티온 - 유기인계 살충제' },
        { value: 'CH026', label: '[살충제] 다이아지논 - 토양 및 엽면 살충제' },
        { value: 'CH027', label: '[살충제] 에티온 - 유기인계 살충제' },
        {
            value: 'CH028',
            label: '[살충제] 옥시데메톤메틸 - 침투이행성 살충제',
        },
        { value: 'CH029', label: '[살충제] 아진포스메틸 - 광범위 살충제' },
        { value: 'CH030', label: '[살충제] 포스파미돈 - 침투이행성 살충제' },
        { value: 'CH031', label: '[살충제] 메빈포스 - 속효성 살충제' },
        { value: 'CH032', label: '[살충제] 메틸파라티온 - 유기인계 살충제' },
        { value: 'CH033', label: '[살충제] 파라티온 - 유기인계 살충제' },
        {
            value: 'CH034',
            label: '[살충제] DDT - 유기염소계 살충제 (사용 금지)',
        },
        { value: 'CH035', label: '[살충제] BHC - 유기염소계 살충제' },
        { value: 'CH036', label: '[살충제] 클로르덴 - 유기염소계 살충제' },
        { value: 'CH037', label: '[살충제] 헵타클로르 - 유기염소계 살충제' },
        { value: 'CH038', label: '[살충제] 톡사펜 - 유기염소계 살충제' },
        { value: 'CH039', label: '[살충제] 알드린 - 유기염소계 살충제' },
        { value: 'CH040', label: '[살충제] 디엘드린 - 유기염소계 살충제' },
        { value: 'CH041', label: '[살충제] 엔드린 - 유기염소계 살충제' },
        { value: 'CH042', label: '[살충제] 메소밀 - 카바메이트계 살충제' },
        { value: 'CH043', label: '[살충제] 티오텍스 - 살충제' },
        {
            value: 'CH044',
            label: '[살충제] 후라단(카보퓨란) - 카바메이트계 살충제',
        },
        { value: 'CH045', label: '[살충제] 엔도설판 - 유기염소계 살충제' },
        { value: 'CH051', label: '[살균제] 캡탄 - 접촉성 살균제' },
        { value: 'CH052', label: '[살균제] 베노밀 - 침투이행성 살균제' },
        { value: 'CH053', label: '[살균제] 지네브 - 보호살균제' },
        { value: 'CH054', label: '[살균제] 마네브 - 보호살균제' },
        { value: 'CH055', label: '[살균제] 만코제브 - 보호살균제' },
        {
            value: 'CH056',
            label: '[살균제] 틸트(프로피코나졸) - 침투이행성 살균제',
        },
        {
            value: 'CH057',
            label: '[미생물제] 근류균 - 두과작물용 질소고정 미생물',
        },
        {
            value: 'CH100',
            label: '[생장조절제] 픽스(메피쿼트클로라이드) - 생장억제제',
        },
        { value: 'CH101', label: '[생장조절제] 에테폰 - 에틸렌 생성촉진제' },
        { value: 'CH102', label: '[생장조절제] 에틸렌 - 식물호르몬' },
    ];

    const applicationMethodOptions: SelectOption[] = [
        { value: 'AP001', label: '표면 살포(혼입 없음) - 가장 기본' },
        { value: 'AP002', label: '표면 살포 후 혼입 - 경운기·괴비 혼입' },
        { value: 'AP003', label: '지표면 밴드 시비 - 줄 시비' },
        { value: 'AP004', label: '지하 밴드 시비 - 조파·줄 비료' },
        { value: 'AP007', label: '심층 구멍 시비 - 벼 비료 깊이 투입' },
        { value: 'AP008', label: '종자 처리 시비 - 식재·파종과 함께' },
        { value: 'AP009', label: '비료 주입 - 액체 비료 사용' },
        { value: 'AP011', label: '침수 토양 표면 살포 - 벼 재배 대표 방식' },
        { value: 'AP018', label: '포화토양 밴드 시비 - 벼 시비에 많이 사용' },
        {
            value: 'AP019',
            label: '요소 대립 심층 투입 - 벼 NH₃ 손실 방지 효과',
        },
    ];

    return (
        <div className="pest-management">
            <div className="row mb-3">
                <div className="col-12 text-end">
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addFarmingHist}
                    >
                        <i className="ri-add-line me-1"></i>
                        병해충관리 추가
                    </button>
                </div>
            </div>

            {farmingHists.length > 0 && (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover mb-3">
                        <thead className="text-center">
                            <tr>
                                <th style={{ width: '15%' }}>일자 선택</th>
                                <th style={{ width: '30%' }}>농약</th>
                                <th style={{ width: '25%' }}>살포방법</th>
                                <th style={{ width: '10%' }}>
                                    처리량
                                    <div className="fs-9">(kg/ha)</div>
                                </th>
                                <th style={{ width: '5%' }}>삭제</th>
                            </tr>
                        </thead>
                        <tbody>
                            {farmingHists.map((record, index) => (
                                <tr key={index}>
                                    <td>
                                        <DatePicker
                                            selectedDate={parseDate(
                                                record.workDt || '',
                                            )}
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'workDt',
                                                    formatDateString(value),
                                                )
                                            }
                                            size="sm"
                                            placeholder="날짜를 선택해주세요"
                                            maxDate={undefined}
                                            showYearDropdown={false}
                                            showMonthDropdown={false}
                                        />
                                    </td>
                                    <td>
                                        <SelectInput
                                            options={pesticideOptions}
                                            value={record.chcod || ''}
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'chcod',
                                                    value,
                                                )
                                            }
                                            placeholder="농약을 선택해주세요"
                                            selectClassName="form-control"
                                        />
                                    </td>
                                    <td>
                                        <SelectInput
                                            options={applicationMethodOptions}
                                            value={record.chme || ''}
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'chme',
                                                    value,
                                                )
                                            }
                                            placeholder="살포방법 선택"
                                            selectClassName="form-control"
                                        />
                                    </td>
                                    <td>
                                        <TextInput
                                            value={
                                                record.chamt
                                                    ? record.chamt.toString()
                                                    : ''
                                            }
                                            onChange={value =>
                                                handleFarmingHistChange(
                                                    index,
                                                    'chamt',
                                                    value,
                                                )
                                            }
                                            placeholder="처리량 입력"
                                            type="number"
                                            numericOnly={true}
                                            allowDecimal={true}
                                        />
                                    </td>
                                    <td className="text-center">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-danger"
                                            onClick={() =>
                                                removeFarmingHist(index)
                                            }
                                            title="삭제"
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {farmingHists.length === 0 && (
                <div className="alert alert-info text-center py-4">
                    <p className="mb-0">
                        <i className="ri-information-line me-2"></i>
                        병해충관리(C) 관련 내용을 입력해주세요. 추가 버튼을 눌러
                        병해충관리 기록을 추가할 수 있습니다.
                    </p>
                </div>
            )}
        </div>
    );
};

export default PestMng;
